import React, { useState, useEffect, useCallback } from 'react';
import { Save, BookOpen, Clock, Settings, Plus, Eye } from 'lucide-react';
import LearningComponentPicker from './LearningComponentPicker';
import LearningComponentsService from '../../services/learningComponentsService';
import './CourseComponentsManager.css';

const CourseComponentsManager = ({ 
  courseId, 
  sessionId, 
  topicId,
  entityType,
  onSave,
  onCancel 
}) => {
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [existingComponents, setExistingComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('components');

  // Use the explicitly passed entityType, or determine from props
  const finalEntityType = entityType || (topicId ? 'topic' : courseId ? 'course' : sessionId ? 'session' : 'topic');
  const entityId = topicId || courseId || sessionId;

  const loadExistingComponents = useCallback(async () => {
    try {
      setLoading(true);
      let components = [];
      
      if (finalEntityType === 'course') {
        components = await LearningComponentsService.getCourseComponents(entityId);
      } else if (finalEntityType === 'session') {
        components = await LearningComponentsService.getSessionComponents(entityId);
      } else if (finalEntityType === 'topic') {
        components = await LearningComponentsService.getTopicComponents(entityId);
      }
      
      setExistingComponents(components);
      setSelectedComponents(components);
    } catch (error) {
      console.error('Error loading existing components:', error);
    } finally {
      setLoading(false);
    }
  }, [entityId, finalEntityType]);

  useEffect(() => {
    loadExistingComponents();
  }, [loadExistingComponents]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      console.log('Saving components:', {
        finalEntityType,
        entityId,
        existingComponents: existingComponents.length,
        selectedComponents: selectedComponents.length
      });
      
      // Get component IDs to add
      const existingIds = existingComponents.map(c => c.id);
      const selectedIds = selectedComponents.map(c => c.id);
      const toAdd = selectedIds.filter(id => !existingIds.includes(id));
      const toRemove = existingIds.filter(id => !selectedIds.includes(id));
      
      console.log('Components to add:', toAdd);
      console.log('Components to remove:', toRemove);

      // Add new components
      if (toAdd.length > 0) {
        if (finalEntityType === 'course') {
          await LearningComponentsService.addComponentsToCourse(entityId, toAdd, 'current-user');
        } else if (finalEntityType === 'session') {
          await LearningComponentsService.addComponentsToSession(entityId, toAdd, 'current-user');
        } else if (finalEntityType === 'topic') {
          await LearningComponentsService.addComponentsToTopic(entityId, toAdd, 'current-user');
        }
      }

      // Remove components
      for (const id of toRemove) {
        if (finalEntityType === 'course') {
          await LearningComponentsService.removeComponentFromCourse(entityId, id);
        } else if (finalEntityType === 'session') {
          await LearningComponentsService.removeComponentFromSession(entityId, id);
        } else if (finalEntityType === 'topic') {
          await LearningComponentsService.removeComponentFromTopic(entityId, id);
        }
      }

      await loadExistingComponents();
      onSave && onSave();
    } catch (error) {
      console.error('Error saving components:', error);
      alert('Error saving components. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getTotalEstimatedTime = () => {
    return selectedComponents.reduce((total, comp) => total + (comp.estimatedTime || 0), 0);
  };

  const getComponentsByType = () => {
    const grouped = {};
    selectedComponents.forEach(comp => {
      if (!grouped[comp.type]) {
        grouped[comp.type] = [];
      }
      grouped[comp.type].push(comp);
    });
    return grouped;
  };

  const getTypeIcon = (type) => {
    const icons = {
      'interactive': '🎯',
      'assessment': '📝',
      'collaborative': '👥',
      'creative': '✨'
    };
    return icons[type] || '📦';
  };

  if (loading) {
    return (
      <div className="components-manager-loading">
        <div className="loading-spinner"></div>
        <p>Loading components...</p>
      </div>
    );
  }

  return (
    <div className="course-components-manager">
      {/* Header */}
      <div className="manager-header">
        <div className="header-info">
          <h3>
            {entityType === 'course' && <BookOpen size={24} />}
            {entityType === 'session' && <Clock size={24} />}
            {entityType === 'topic' && <Settings size={24} />}
            Learning Components
          </h3>
          <p className="entity-type">
            Managing: {entityType} {entityId?.slice(0, 8)}...
          </p>
        </div>
        
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{selectedComponents.length}</span>
            <span className="stat-label">Components</span>
          </div>
          <div className="stat">
            <span className="stat-value">{getTotalEstimatedTime()}m</span>
            <span className="stat-label">Total Time</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="manager-tabs">
        <button
          className={`tab ${activeTab === 'components' ? 'active' : ''}`}
          onClick={() => setActiveTab('components')}
        >
          <Plus size={18} />
          Add Components
        </button>
        <button
          className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          <Eye size={18} />
          Preview ({selectedComponents.length})
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={18} />
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="manager-content">
        {activeTab === 'components' && (
          <LearningComponentPicker
            onComponentsSelected={setSelectedComponents}
            selectedComponents={selectedComponents}
            maxComponents={20}
          />
        )}

        {activeTab === 'preview' && (
          <div className="components-preview">
            <h4>Selected Components Preview</h4>
            
            {selectedComponents.length === 0 ? (
              <div className="empty-state">
                <p>No components selected yet.</p>
                <button
                  className="add-components-btn"
                  onClick={() => setActiveTab('components')}
                >
                  Add Components
                </button>
              </div>
            ) : (
              <div className="preview-content">
                {Object.entries(getComponentsByType()).map(([type, components]) => (
                  <div key={type} className="component-group">
                    <h5>
                      {getTypeIcon(type)} {type.charAt(0).toUpperCase() + type.slice(1)} Components
                    </h5>
                    <div className="component-list">
                      {components.map(comp => (
                        <div key={comp.id} className="component-preview-item">
                          <div className="item-header">
                            <h6>{comp.name}</h6>
                            <span className="time-badge">
                              <Clock size={14} /> {comp.estimatedTime}m
                            </span>
                          </div>
                          <p className="item-description">{comp.description}</p>
                          <div className="item-tags">
                            {comp.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="tag">{tag}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="components-settings">
            <h4>Component Settings</h4>
            
            <div className="settings-section">
              <h5>General Settings</h5>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Allow students to skip components
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Show progress indicators
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" />
                  Require completion in order
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h5>Time Settings</h5>
              <div className="setting-item">
                <label>
                  Maximum time per component:
                  <input type="number" defaultValue="30" min="5" max="180" />
                  minutes
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Show time warnings
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h5>Feedback Settings</h5>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Enable instant feedback
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" />
                  Allow peer review
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="manager-actions">
        <button
          className="cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="btn-spinner"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save Components
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CourseComponentsManager;
